import { type ReactNode, memo, useMemo } from 'react';

import type { ChatContextData } from '@eluve/api-contract';
import { Stack, Text, tv } from '@eluve/components';

import { CopyRichTextButton } from '../CopyRichTextButton';
import { InterimResultLoading } from '../InterimResultLoading';

import { Citation, CitationText } from './CitationText';
import { ConversationStarterSaved } from './ConversationStarterSaved';
import { ConversationStarterSuggestionCard } from './ConversationStarterSuggestionCard';
import { CustomInstructionSuggestionCard } from './CustomInstructionSuggestionCard';
import { DocumentPreviewCard } from './DocumentPreviewCard';
import { MemoizedMarkdown } from './MemoizedMarkdown';
import { StartingChatMessage } from './StartingChatMessageCard';
import { ToolCallStatus, ToolInvocation } from './ToolCallStatus';
import { ToolCompletionEffect } from './ToolCompletionEffect';

export type ChatMessage = {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'data';
  content: string;
  isStreaming?: boolean;
  citations?: Citation[];
  toolInvocations?: ToolInvocation[];
  contexts?: ChatContextData[];
};

type ChatMessageBubbleProps = {
  message: ChatMessage;
  isStreaming?: boolean;
  templateMessages?: StartingChatMessage[];
  onCopy?: (message: ChatMessage) => void;
  onMessageSend?: (content: string) => void;
  onStarterSaved?: () => void;
  onNoteUpdated?: () => void;
  onCustomInstructionSaved?: () => void;
  onDocumentAdded?: (doc?: { id: string; title: string }) => void;
  onDocumentCommit?: (
    draft: { title: string; content: string; docType?: string },
    edited: boolean,
  ) => Promise<void>;
  onSourceClick?: (citation: Citation) => void;
  children?: ReactNode;
};

const chatMessageStyles = tv({
  base: 'posthog-privacy-text group relative rounded-3xl p-4 break-words',
  variants: {
    role: {
      user: 'w-auto max-w-[80%] self-end bg-bg-secondary text-text-primary',
      assistant: 'w-full self-start text-text-primary',
      system: 'hidden',
      data: 'hidden',
    },
  },
});

const SUGGESTION_TOOL_NAME = 'suggestConversationStarter';
const SAVE_TOOL_NAME = 'saveConversationStarter';
const UPDATE_NOTE_TOOL_NAME = 'updateAppointmentNote';
const SUGGEST_INSTRUCTION_TOOL_NAME = 'suggestCustomInstruction';
const SAVE_INSTRUCTION_TOOL_NAME = 'saveCustomInstruction';
const DRAFT_DOCUMENT_TOOL_NAME = 'draftDocument';
const GENERATE_DOCUMENT_TOOL_NAME = 'generateDocument';

const ChatMessageBubbleComponent = ({
  message,
  isStreaming,
  templateMessages,
  onCopy,
  onMessageSend,
  onStarterSaved,
  onNoteUpdated,
  onCustomInstructionSaved,
  onDocumentAdded,
  onDocumentCommit,
  onSourceClick,
  children,
}: ChatMessageBubbleProps) => {
  const hasCitations = message.citations && message.citations.length > 0;
  // Filter citations to ensure valid indices
  const validCitations =
    hasCitations && message.citations
      ? message.citations.filter(
          (citation) =>
            typeof citation.startIndex === 'number' &&
            typeof citation.endIndex === 'number',
        )
      : [];

  const userMessageContent = useMemo(() => {
    if (message.role !== 'user') return null;

    // If the message matches a starting message template, display its title instead
    const matchingTemplate = templateMessages?.find(
      (template) =>
        template.content.replace(/ +/g, ' ').trim() ===
        message.content.replace(/ +/g, ' ').trim(),
    );

    if (matchingTemplate) {
      return matchingTemplate.title;
    }

    // Otherwise, render the original message content with line breaks
    return message.content.split('\n').map((line, index, splitMessage) => (
      <span key={index}>
        {line}
        {index < splitMessage.length - 1 && <br />}
      </span>
    ));
  }, [message.content, message.role, templateMessages]);

  return (
    <Stack
      aria-label={
        message.role === 'user' ? 'User message' : 'Assistant message'
      }
      className={chatMessageStyles({ role: message.role })}
      gap={2}
    >
      {message.role === 'assistant' && (
        <div className="absolute top-0.5 right-0.5 opacity-0 transition-opacity duration-200 ease-linear group-hover:opacity-100">
          <CopyRichTextButton
            markdownContent={message.content}
            onCopy={() => onCopy?.(message)}
          />
        </div>
      )}
      {message.toolInvocations
        ?.filter(
          (tool) =>
            tool.toolName !== SUGGESTION_TOOL_NAME &&
            tool.toolName !== SAVE_TOOL_NAME &&
            tool.toolName !== SUGGEST_INSTRUCTION_TOOL_NAME &&
            tool.toolName !== DRAFT_DOCUMENT_TOOL_NAME,
        )
        .map((tool) => (
          <ToolCallStatus key={tool.toolCallId} toolInvocation={tool} />
        ))}
      {message.role === 'user' ? (
        <Text color="inherit">{userMessageContent}</Text>
      ) : validCitations.length > 0 ? (
        <CitationText
          text={message.content}
          citations={validCitations}
          onSourceClick={onSourceClick}
        />
      ) : (
        <MemoizedMarkdown content={message.content} id={message.id} />
      )}
      {message.toolInvocations?.map((tool) => {
        if (tool.toolName === SUGGESTION_TOOL_NAME) {
          return (
            <ConversationStarterSuggestionCard
              key={tool.toolCallId}
              toolInvocation={tool}
              onConfirm={(msg) => onMessageSend?.(msg)}
              onDismiss={(msg) => onMessageSend?.(msg)}
            />
          );
        }
        if (tool.toolName === SAVE_TOOL_NAME) {
          return (
            <ConversationStarterSaved
              key={tool.toolCallId}
              toolInvocation={tool}
              onSaved={() => onStarterSaved?.()}
            />
          );
        }
        if (tool.toolName === UPDATE_NOTE_TOOL_NAME && onNoteUpdated) {
          return (
            <ToolCompletionEffect
              key={tool.toolCallId}
              toolInvocation={tool}
              onComplete={onNoteUpdated}
            />
          );
        }
        if (tool.toolName === SUGGEST_INSTRUCTION_TOOL_NAME) {
          return (
            <CustomInstructionSuggestionCard
              key={tool.toolCallId}
              toolInvocation={tool}
              onConfirm={(msg) => onMessageSend?.(msg)}
              onDismiss={(msg) => onMessageSend?.(msg)}
            />
          );
        }
        if (
          tool.toolName === SAVE_INSTRUCTION_TOOL_NAME &&
          onCustomInstructionSaved
        ) {
          return (
            <ToolCompletionEffect
              key={tool.toolCallId}
              toolInvocation={tool}
              onComplete={onCustomInstructionSaved}
            />
          );
        }
        if (tool.toolName === DRAFT_DOCUMENT_TOOL_NAME) {
          return (
            <DocumentPreviewCard
              key={tool.toolCallId}
              toolInvocation={tool}
              onCommit={onDocumentCommit}
              onDiscard={(msg) => onMessageSend?.(msg)}
            />
          );
        }
        if (tool.toolName === GENERATE_DOCUMENT_TOOL_NAME && onDocumentAdded) {
          return (
            <ToolCompletionEffect
              key={tool.toolCallId}
              toolInvocation={tool}
              onComplete={() => {
                const output =
                  tool.state === 'output-available'
                    ? (tool.output as { id?: string; title?: string })
                    : undefined;
                onDocumentAdded(
                  output?.id
                    ? { id: output.id, title: output.title ?? '' }
                    : undefined,
                );
              }}
            />
          );
        }
        return null;
      })}
      {children}
      {isStreaming && <InterimResultLoading />}
    </Stack>
  );
};

export const ChatMessageBubble = memo(
  ChatMessageBubbleComponent,
  (prevProps, nextProps) => {
    if (prevProps.message.id !== nextProps.message.id) return false;
    if (prevProps.message.content !== nextProps.message.content) return false;
    if (
      prevProps.message.toolInvocations?.length !==
      nextProps.message.toolInvocations?.length
    )
      return false;
    const prevStates = prevProps.message.toolInvocations
      ?.map((t) => t.state)
      .join(',');
    const nextStates = nextProps.message.toolInvocations
      ?.map((t) => t.state)
      .join(',');
    if (prevStates !== nextStates) return false;
    if (prevProps.isStreaming !== nextProps.isStreaming) return false;
    if (prevProps.children !== nextProps.children) return false;
    return true;
  },
);

ChatMessageBubble.displayName = 'ChatMessageBubble';
